import { Component, effect, inject, input, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { ExpenseClaim, ExpenseClaimStatus } from '../../../models/expense-claim/expense-claim';
import { ExpenseClaimProxyService } from '../../../proxies/expense-claim.proxy.service';
import { Currency, currencySymbol } from '../../../utilities/currency.util';
import { expenseClaimStatusLabel, expenseClaimStatusSeverity } from '../expense-claim-status.util';

/** The latest claims shown on a fund's or trip's page. */
const OWNER_CLAIMS_LIMIT = 10;

/** The expense claims of one fund or trip (#5800), newest first, for its details page. */
@Component({
  selector: 'app-owner-claims',
  templateUrl: './owner-claims.component.html',
})
export class OwnerClaimsComponent {
  private readonly proxy = inject(ExpenseClaimProxyService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);

  /** The fund's or trip's id. */
  readonly ownerReference = input.required<string>();

  readonly isLoading = signal(false);
  readonly claims = signal<ExpenseClaim[]>([]);

  constructor() {
    effect(() => this.load(this.ownerReference()), { allowSignalWrites: true });
  }

  private load(ownerReference: string): void {
    this.isLoading.set(true);
    this.proxy
      .search({ ownerReference }, 1, OWNER_CLAIMS_LIMIT, null, -1)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe((response) => this.claims.set(response.data?.items ?? []));
  }

  open(claim: ExpenseClaim): void {
    this.router.navigate(['/expense-claim-details'], { queryParams: { id: claim.id } });
  }

  statusLabel(status: ExpenseClaimStatus): string {
    return expenseClaimStatusLabel(status, (key, fallback) => {
      const value = this.translate.instant(key);
      return value === key ? fallback : value;
    });
  }

  statusSeverity(status: ExpenseClaimStatus): 'success' | 'secondary' | 'warn' | 'info' | 'danger' {
    return expenseClaimStatusSeverity(status);
  }

  symbolOf(currency: Currency): string {
    return currencySymbol(currency);
  }

  getRow(rowData: unknown): ExpenseClaim {
    return rowData as ExpenseClaim;
  }
}
